import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const SiteImagesContext = createContext({ images: {}, loaded: false, refresh: () => {} });

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetches every site-image slot once on mount and caches in context.
 * Individual components read a single slot via useSiteImage('the.key', DEFAULT).
 * Admin panel calls refresh() after an upload to re-sync all consumers.
 */
export const SiteImagesProvider = ({ children }) => {
  const [images, setImages] = useState({});
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/site-images`);
      setImages(res.data || {});
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('site-images fetch failed:', err);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  const value = useMemo(() => ({ images, loaded, refresh: load }), [images, loaded]);
  return <SiteImagesContext.Provider value={value}>{children}</SiteImagesContext.Provider>;
};

/**
 * Read a single slot URL. Returns `fallback` if the slot hasn't loaded yet
 * (or doesn't exist in the registry), so pages never render `undefined`.
 */
export const useSiteImage = (slot, fallback = '') => {
  const { images } = useContext(SiteImagesContext);
  return images[slot]?.url || fallback;
};

export const useSiteImagesContext = () => useContext(SiteImagesContext);
